import React from 'react';
import { Shield, Cookie, Database, Mail, Lock, Eye, FileText } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-amber-900 to-slate-900 text-white py-20 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500/20 rounded-full mb-6">
            <Shield className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-slate-300 text-lg">
            Last updated: January 1, 2026
          </p>
        </div>

        {/* Introduction */}
        <Section>
          <p className="text-slate-300 leading-relaxed">
            Africamed Business Forum ("we", "our", or "us") is committed to protecting your privacy. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
            when you visit our website and register for our conference events.
          </p>
          <p className="text-slate-300 leading-relaxed mt-4">
            Please read this privacy policy carefully. If you do not agree with the terms of this 
            privacy policy, please do not access the site.
          </p>
        </Section>

        {/* Information We Collect */}
        <Section icon={<Database />} title="Information We Collect">
          <Subsection title="Personal Information">
            <p className="text-slate-300 leading-relaxed mb-3">
              We collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
              <li>Register for the Africamed Business Forum conference</li>
              <li>Apply for Club Africamed membership</li>
              <li>Subscribe to our newsletter or communications</li>
              <li>Contact us via email or contact forms</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-3">
              This information may include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
              <li>Full name and title</li>
              <li>Email address and phone number</li>
              <li>Organization/company name and position</li>
              <li>Country and city of residence</li>
              <li>Professional information and interests</li>
              <li>Payment information (processed securely through third-party providers)</li>
            </ul>
          </Subsection>

          <Subsection title="Automatically Collected Information">
            <p className="text-slate-300 leading-relaxed">
              When you visit our website, we automatically collect certain information about your device, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4 mt-3">
              <li>IP address and browser type</li>
              <li>Operating system and device information</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website or source</li>
              <li>Click patterns and navigation paths</li>
            </ul>
          </Subsection>
        </Section>

        {/* Cookies */}
        <Section icon={<Cookie />} title="Cookies and Tracking Technologies">
          <p className="text-slate-300 leading-relaxed mb-4">
            We use cookies and similar tracking technologies to enhance your browsing experience, 
            analyze site traffic, and understand where our visitors are coming from.
          </p>
          
          <Subsection title="Types of Cookies We Use">
            <div className="space-y-4">
              <CookieType 
                name="Necessary Cookies" 
                description="Essential for the website to function properly. These cookies enable core functionality such as security, network management, and accessibility."
              />
              <CookieType 
                name="Analytics Cookies" 
                description="Help us understand how visitors interact with our website by collecting and reporting information anonymously. We use Vercel Analytics for this purpose."
              />
              <CookieType 
                name="Preference Cookies" 
                description="Allow the website to remember your cookie consent choices and language preferences."
              />
            </div>
          </Subsection>

          <p className="text-slate-300 leading-relaxed mt-4">
            You can control cookie preferences through our cookie consent banner or your browser settings. 
            Note that disabling certain cookies may impact website functionality.
          </p>
        </Section>

        {/* How We Use Your Information */}
        <Section icon={<Eye />} title="How We Use Your Information">
          <p className="text-slate-300 leading-relaxed mb-3">
            We use the information we collect for the following purposes:
          </p>
          <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
            <li>To process your conference registrations and membership applications</li>
            <li>To communicate with you about events, updates, and important information</li>
            <li>To send you marketing communications (with your consent)</li>
            <li>To improve our website and user experience</li>
            <li>To analyze website usage and trends</li>
            <li>To prevent fraud and enhance security</li>
            <li>To comply with legal obligations</li>
            <li>To respond to your inquiries and provide customer support</li>
          </ul>
        </Section>

        {/* Third-Party Services */}
        <Section icon={<FileText />} title="Third-Party Services">
          <p className="text-slate-300 leading-relaxed mb-3">
            We use the following third-party services that may collect and process your data:
          </p>
          
          <div className="space-y-4">
            <ThirdPartyService 
              name="Supabase" 
              purpose="Database and backend services for storing registration and membership data"
              link="https://supabase.com/privacy"
            />
            <ThirdPartyService 
              name="Vercel Analytics" 
              purpose="Website analytics and performance monitoring"
              link="https://vercel.com/legal/privacy-policy"
            />
            <ThirdPartyService 
              name="Email Service Providers" 
              purpose="Sending transactional and marketing emails"
            />
          </div>

          <p className="text-slate-300 leading-relaxed mt-4">
            These third-party services have their own privacy policies governing the use of your information. 
            We encourage you to review their privacy policies.
          </p>
        </Section>

        {/* Data Security */}
        <Section icon={<Lock />} title="Data Security">
          <p className="text-slate-300 leading-relaxed mb-4">
            We implement appropriate technical and organizational security measures to protect your 
            personal information against unauthorized access, alteration, disclosure, or destruction.
          </p>
          <p className="text-slate-300 leading-relaxed mb-4">
            These measures include:
          </p>
          <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
            <li>Encryption of data in transit and at rest</li>
            <li>Secure database hosting with access controls</li>
            <li>Regular security audits and updates</li>
            <li>Limited access to personal data by authorized personnel only</li>
          </ul>
          <p className="text-slate-300 leading-relaxed mt-4">
           
          </p>
        </Section>

        {/* Data Retention */}
        <Section title="Data Retention">
          <p className="text-slate-300 leading-relaxed">
            We retain your personal information only for as long as necessary to fulfill the purposes 
            outlined in this privacy policy, unless a longer retention period is required or permitted by law.
          </p>
          <p className="text-slate-300 leading-relaxed mt-4">
            Conference registration data is retained for a minimum of 3 years for administrative and 
            legal purposes. After this period, data may be anonymized or deleted.
          </p>
        </Section>

        {/* Your Rights */}
        <Section title="Your Privacy Rights">
          <p className="text-slate-300 leading-relaxed mb-3">
            Depending on your location, you may have the following rights regarding your personal information:
          </p>
          <ul className="list-disc list-inside space-y-2 text-slate-300 ml-4">
            <li><strong className="text-amber-400">Right to Access:</strong> Request copies of your personal data</li>
            <li><strong className="text-amber-400">Right to Rectification:</strong> Request correction of inaccurate data</li>
            <li><strong className="text-amber-400">Right to Erasure:</strong> Request deletion of your personal data</li>
            <li><strong className="text-amber-400">Right to Restrict Processing:</strong> Request limitation of data processing</li>
            <li><strong className="text-amber-400">Right to Data Portability:</strong> Receive your data in a portable format</li>
            <li><strong className="text-amber-400">Right to Object:</strong> Object to certain data processing activities</li>
            <li><strong className="text-amber-400">Right to Withdraw Consent:</strong> Withdraw consent for data processing at any time</li>
          </ul>
          <p className="text-slate-300 leading-relaxed mt-4">
            To exercise any of these rights, please contact us using the information provided below.
          </p>
        </Section>

        {/* International Transfers */}
        <Section title="International Data Transfers">
          <p className="text-slate-300 leading-relaxed">
            Your information may be transferred to and processed in countries other than your country of residence. 
            These countries may have data protection laws that are different from the laws of your country.
          </p>
          <p className="text-slate-300 leading-relaxed mt-4">
            We ensure that such transfers comply with applicable data protection laws and that your data 
            remains protected according to this privacy policy.
          </p>
        </Section>

        {/* Children's Privacy */}
        <Section title="Children's Privacy">
          <p className="text-slate-300 leading-relaxed">
            Our services are not directed to individuals under the age of 18. We do not knowingly collect 
            personal information from children. If you are a parent or guardian and believe your child has 
            provided us with personal information, please contact us, and we will delete such information.
          </p>
        </Section>

        {/* Changes to Privacy Policy */}
        <Section title="Changes to This Privacy Policy">
          <p className="text-slate-300 leading-relaxed">
            We may update this privacy policy from time to time. The updated version will be indicated by 
            an updated "Last Updated" date at the top of this page. We encourage you to review this privacy 
            policy periodically to stay informed about how we protect your information.
          </p>
          <p className="text-slate-300 leading-relaxed mt-4">
            Significant changes will be communicated through email or prominent notice on our website.
          </p>
        </Section>

        {/* Contact Information */}
        <Section icon={<Mail />} title="Contact Us">
          <div className="bg-gradient-to-r from-amber-500/10 to-slate-800/50 rounded-xl p-6 border border-amber-500/30">
            <p className="text-slate-300 leading-relaxed mb-4">
              If you have questions or concerns about this privacy policy or our data practices, 
              please contact us at:
            </p>
            <div className="space-y-2">
              <p className="text-white font-semibold">Africamed Business Forum</p>
              <p className="text-slate-300">Email: <a href="mailto:it-support@africamedforum.com" className="text-amber-400 hover:text-amber-300 underline">privacy@africamed.com</a></p>
              <p className="text-slate-300">General Inquiries: <a href="mailto:info@africamed.com" className="text-amber-400 hover:text-amber-300 underline">info@africamed.com</a></p>
            </div>
          </div>
        </Section>

        {/* GDPR/CCPA Notice */}
        <div className="mt-12 p-6 bg-slate-800/50 rounded-xl border border-slate-700">
          <p className="text-sm text-slate-400 leading-relaxed">
            <strong className="text-amber-400">For EU Residents (GDPR):</strong> We process your personal data 
            in accordance with the General Data Protection Regulation (GDPR). Our legal basis for processing 
            includes consent, contractual necessity, and legitimate interests.
          </p>
          <p className="text-sm text-slate-400 leading-relaxed mt-3">
            <strong className="text-amber-400">For California Residents (CCPA):</strong> You have specific 
            rights under the California Consumer Privacy Act, including the right to know what personal 
            information we collect, the right to delete your information, and the right to opt-out of 
            the sale of your information (we do not sell personal information).
          </p>
        </div>
      </div>
    </div>
  );
}

// Helper Components
function Section({ icon, title, children }) {
  return (
    <div className="mb-12">
      {title && (
        <div className="flex items-center gap-3 mb-6">
          {icon && <div className="text-amber-400">{icon}</div>}
          <h2 className="text-2xl md:text-3xl font-bold text-white">{title}</h2>
        </div>
      )}
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );
}

function Subsection({ title, children }) {
  return (
    <div className="mt-6">
      <h3 className="text-xl font-semibold text-amber-400 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function CookieType({ name, description }) {
  return (
    <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
      <h4 className="font-semibold text-amber-400 mb-2">{name}</h4>
      <p className="text-sm text-slate-300">{description}</p>
    </div>
  );
}

function ThirdPartyService({ name, purpose, link }) {
  return (
    <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
      <h4 className="font-semibold text-white mb-2">{name}</h4>
      <p className="text-sm text-slate-300 mb-2">{purpose}</p>
      {link && (
        <a 
          href={link} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-sm text-amber-400 hover:text-amber-300 underline"
        >
          View their privacy policy →
        </a>
      )}
    </div>
  );
}
